import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { LocaleLink } from "@/components/LocaleLink";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import SEOHead from "@/components/seo/SEOHead";
import JsonLd from "@/components/seo/JsonLd";
import { Calendar, User, Tag, ArrowLeft } from "lucide-react";

const BASE_URL = "https://www.craftfindus.com";

interface BlogPostData {
  id: string;
  locale_prefix: string;
  slug: string;
  title: string;
  meta_description: string;
  content_html: string;
  excerpt: string;
  author_name: string;
  category: string;
  tags: string[];
  featured_image_url: string;
  published_at: string;
}

export function BlogList() {
  const { locale } = useParams<{ locale: string }>();
  const { t } = useTranslation();
  const [posts, setPosts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!locale) return;
    (async () => {
      const { data } = await supabase
        .from("blog_posts")
        .select("id, slug, title, excerpt, author_name, category, published_at, featured_image_url")
        .eq("locale_prefix", locale)
        .eq("is_published", true)
        .order("published_at", { ascending: false })
        .limit(50);
      setPosts(data || []);
      setLoading(false);
    })();
  }, [locale]);

  return (
    <>
      <Header />
      <main id="main-content" className="bg-background min-h-screen">
        <SEOHead title={t("blog.title")} description={t("blog.description")} canonical="/blog" />
        <JsonLd breadcrumbs={[
          { name: "CRAFTFINDUS", url: `${BASE_URL}/${locale}/` },
          { name: t("blog.title"), url: `${BASE_URL}/${locale}/blog` },
        ]} />
        <section className="py-16 md:py-24">
          <div className="container max-w-4xl mx-auto px-4">
            <h1 className="text-3xl md:text-4xl font-bold mb-8">{t("blog.title")}</h1>
            {loading ? (
              <p className="text-caption">{t("common.loading")}</p>
            ) : posts.length === 0 ? (
              <p className="text-muted-foreground">{t("blog.noPosts")}</p>
            ) : (
              <div className="space-y-8">
                {posts.map((post) => (
                  <article key={post.id} className="border border-border rounded-sm p-6 hover:bg-secondary/20 transition-colors">
                    <LocaleLink to={`/blog/${post.slug}`}>
                      <h2 className="text-xl font-semibold mb-2 hover:text-primary transition-colors">{post.title}</h2>
                    </LocaleLink>
                    <div className="flex flex-wrap gap-4 text-xs text-muted-foreground mb-3">
                      {post.published_at && (
                        <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{new Date(post.published_at).toLocaleDateString()}</span>
                      )}
                      {post.author_name && (
                        <span className="flex items-center gap-1"><User className="w-3 h-3" />{post.author_name}</span>
                      )}
                      {post.category && (
                        <span className="flex items-center gap-1"><Tag className="w-3 h-3" />{post.category}</span>
                      )}
                    </div>
                    {post.excerpt && <p className="text-sm text-muted-foreground">{post.excerpt}</p>}
                  </article>
                ))}
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}

export function BlogPost() {
  const { slug, locale } = useParams<{ slug: string; locale: string }>();
  const { t } = useTranslation();
  const [post, setPost] = useState<BlogPostData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!slug || !locale) return;
    (async () => {
      const { data } = await supabase
        .from("blog_posts")
        .select("*")
        .eq("locale_prefix", locale)
        .eq("slug", slug)
        .eq("is_published", true)
        .single();
      setPost(data as any);
      setLoading(false);
    })();
  }, [slug, locale]);

  if (loading) return <><Header /><main className="min-h-screen bg-background flex items-center justify-center"><p className="text-caption">{t("common.loading")}</p></main></>;
  if (!post) return null;

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: post.title,
    description: post.meta_description || post.excerpt,
    author: { "@type": "Person", name: post.author_name || "CRAFTFINDUS Redaktion" },
    publisher: { "@type": "Organization", name: "CRAFTFINDUS", url: BASE_URL },
    datePublished: post.published_at,
    ...(post.featured_image_url ? { image: post.featured_image_url } : {}),
  };

  return (
    <>
      <Header />
      <main id="main-content" className="bg-background min-h-screen">
        <SEOHead title={post.title} description={post.meta_description || post.excerpt || ""} canonical={`/blog/${slug}`} type="article" />
        <JsonLd
          breadcrumbs={[
            { name: "CRAFTFINDUS", url: `${BASE_URL}/${locale}/` },
            { name: t("blog.title"), url: `${BASE_URL}/${locale}/blog` },
            { name: post.title, url: `${BASE_URL}/${locale}/blog/${slug}` },
          ]}
          customSchemas={[articleSchema]}
        />
        <article className="py-16 md:py-24">
          <div className="container max-w-3xl mx-auto px-4">
            <LocaleLink to="/blog" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-6">
              <ArrowLeft className="w-3.5 h-3.5" /> {t("blog.backToList")}
            </LocaleLink>
            <h1 className="text-3xl md:text-4xl font-bold mb-4">{post.title}</h1>
            <div className="flex flex-wrap gap-4 text-xs text-muted-foreground mb-8">
              {post.published_at && <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{new Date(post.published_at).toLocaleDateString()}</span>}
              {post.author_name && <span className="flex items-center gap-1"><User className="w-3 h-3" />{post.author_name}</span>}
            </div>
            {post.content_html && (
              <div className="prose prose-sm max-w-none text-foreground" dangerouslySetInnerHTML={{ __html: post.content_html }} />
            )}
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}
